import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  TablePagination,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  questionPaperService,
  QuestionBank,
  Chapter,
  Question,
  QuestionType,
  QuestionImportance,
} from '../../services/questionPaperService';
import { useDebounce } from '../../hooks/useDebounce';
import MathTextRenderer from '../shared/MathTextRenderer';
import AnswerBadge from '../shared/AnswerBadge';
import { elegantMenuProps } from '../../utils/menuStyles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface QuestionBankBrowserProps {
  questionBanks: QuestionBank[];
  chapters: Chapter[];
  sectionIndex: number;
  onAddQuestion: (sectionIndex: number, question: Question) => void;
  // Question ids already present in the section being edited. The browser
  // uses this to render a muted state + an "Already in section" pill and to
  // disable the Add button, so users can't double-add the same question.
  alreadyAddedIds?: Set<string>;
}


const QuestionBankBrowser = memo<QuestionBankBrowserProps>(({
  questionBanks,
  chapters,
  sectionIndex,
  onAddQuestion,
  alreadyAddedIds,
}) => {
  // All browser state is local — each section gets its own independent browser
  const [activeTab, setActiveTab] = useState(0);
  const [selectedBank, setSelectedBank] = useState<QuestionBank | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);
  const [questionsPage, setQuestionsPage] = useState(0);
  const [questionsRowsPerPage, setQuestionsRowsPerPage] = useState(10);
  const [questionsTotalCount, setQuestionsTotalCount] = useState(0);
  const [questionSearchQuery, setQuestionSearchQuery] = useState('');
  const debouncedQuestionSearchQuery = useDebounce(questionSearchQuery, 500);
  const [questionTypeFilter, setQuestionTypeFilter] = useState<QuestionType | ''>('');
  const [questionImportanceFilter, setQuestionImportanceFilter] = useState<QuestionImportance | ''>('');

  // Only the newest request may fill the list; a slower older one is dropped.
  const questionsRequest = useRef(0);

  // A new search or filter starts again from the first page.
  useEffect(() => {
    setQuestionsPage(0);
  }, [debouncedQuestionSearchQuery, questionTypeFilter, questionImportanceFilter]);

  const fetchQuestions = useCallback(async () => {
    if (!selectedChapter) return;
    const request = ++questionsRequest.current;
    try {
      const params: any = {
        page: questionsPage + 1,
        limit: questionsRowsPerPage,
      };
      if (debouncedQuestionSearchQuery) params.search = debouncedQuestionSearchQuery;
      if (questionTypeFilter) params.type = questionTypeFilter;
      if (questionImportanceFilter) params.importance = questionImportanceFilter;

      const response = await questionPaperService.getQuestions(selectedChapter._id, params);
      if (request !== questionsRequest.current) return;
      setAvailableQuestions(response.questions || []);
      setQuestionsTotalCount(response.pagination?.totalItems || 0);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    }
  }, [selectedChapter, questionsPage, questionsRowsPerPage, debouncedQuestionSearchQuery, questionTypeFilter, questionImportanceFilter]);

  useEffect(() => {
    if (selectedChapter) fetchQuestions();
  }, [selectedChapter, fetchQuestions]);

  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>Add from Question Bank</Typography>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        TabIndicatorProps={{
          style: { backgroundColor: 'primary.main', height: 3 }
        }}
        sx={{ 
          mb: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 48,
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 600
            }
          }
        }}
      >
        <Tab label="Select Bank" />
        <Tab label="Select Chapter" disabled={!selectedBank} />
        <Tab label="Select Questions" disabled={!selectedChapter} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <Box>
          {questionBanks.length === 0 ? (
            <Alert severity="info">No question banks found for selected class and subject</Alert>
          ) : (
            <Grid container spacing={1}>
              {questionBanks.map((bank) => {
                const classInfo = typeof bank.classId === 'object' ? bank.classId : null;
                const subjectInfo = typeof bank.subjectId === 'object' ? bank.subjectId : null;
                return (
                  <Grid size={{ xs: 12, sm: 6 }} key={bank._id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selectedBank?._id === bank._id ? 2 : 1,
                        borderColor: selectedBank?._id === bank._id ? 'primary.main' : 'divider'
                      }}
                      onClick={() => {
                        setSelectedBank(bank);
                        setActiveTab(1);
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle2">
                          {classInfo?.name} - {subjectInfo?.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Box>
          {chapters.length === 0 ? (
            <Alert severity="info">No chapters found in this question bank</Alert>
          ) : (
            <Grid container spacing={1}>
              {chapters.map((chapter) => (
                <Grid size={{ xs: 12, sm: 6 }} key={chapter._id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedChapter?._id === chapter._id ? 2 : 1,
                      borderColor: selectedChapter?._id === chapter._id ? 'primary.main' : 'divider'
                    }}
                    onClick={() => {
                      setSelectedChapter(chapter);
                      setActiveTab(2);
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle2">{chapter.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {chapter.questionCount || 0} questions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Box>
          {/* Question Filters */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search questions, tag, past years..."
              value={questionSearchQuery}
              onChange={(e) => setQuestionSearchQuery(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select MenuProps={elegantMenuProps}
                value={questionTypeFilter}
                label="Type"
                onChange={(e) => setQuestionTypeFilter(e.target.value as QuestionType | '')}
              >
                <MenuItem value="">All</MenuItem>
                {Object.values(QuestionType).map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Importance</InputLabel>
              <Select MenuProps={elegantMenuProps}
                value={questionImportanceFilter}
                label="Importance"
                onChange={(e) => setQuestionImportanceFilter(e.target.value as QuestionImportance | '')}
              >
                <MenuItem value="">All</MenuItem>
                {Object.values(QuestionImportance).map((imp) => (
                  <MenuItem key={imp} value={imp}>{imp}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Questions List */}
          {availableQuestions.length === 0 ? (
            <Alert severity="info">No questions found</Alert>
          ) : (
            <>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {availableQuestions.map((question) => {
                  const isAlreadyAdded = !!alreadyAddedIds?.has(question._id);
                  return (
                  <Card
                    key={question._id}
                    sx={{ mb: 1, p: 1.5, opacity: isAlreadyAdded ? 0.55 : 1 }}
                    variant="outlined"
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Box flex={1}>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                          <MathTextRenderer text={question.questionText} />
                        </Typography>
                        {question.type === 'MCQ' && question.options && question.options.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {question.options.map((opt: string, idx: number) => (
                              <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                {String.fromCharCode(65 + idx)}) <MathTextRenderer text={opt} />
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {question.subParts && question.subParts.length > 0 && (
                          <Box sx={{ mt: 0.5, ml: 1.5 }}>
                            {question.subParts.map((part, idx) => {
                              const roman = ['i','ii','iii','iv','v','vi','vii','viii','ix','x','xi','xii','xiii','xiv','xv'];
                              return (
                                <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                                  <Box component="span" sx={{ fontWeight: 600, color: 'primary.main', mr: 0.5 }}>({roman[idx] || idx + 1})</Box>
                                  <MathTextRenderer text={part.text} />
                                </Typography>
                              );
                            })}
                          </Box>
                        )}
                        <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                          <Chip 
                            label={question.type} 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                          />
                          <Chip 
                            label={question.importance} 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                          />
                          {question.metadata?.marks && (
                            <Chip 
                              label={`${question.metadata.marks} marks`} 
                              size="small" 
                              sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                            />
                          )}
                          <AnswerBadge dense answer={question.answer} answerImages={question.answerImages} />
                          {question.metadata?.tags && question.metadata.tags.length > 0 && (
                            question.metadata.tags.map((tag, idx) => (
                              <Chip 
                                key={idx} 
                                label={tag} 
                                size="small" 
                                variant="outlined"
                                color="primary"
                                sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                              />
                            ))
                          )}
                          {question.metadata?.pastPaperReferences && question.metadata.pastPaperReferences.length > 0 && (
                            question.metadata.pastPaperReferences.map((ref, idx) => (
                              <Chip
                                key={idx}
                                label={`Past: ${ref}`}
                                size="small"
                                variant="outlined"
                                color="secondary"
                                sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                              />
                            ))
                          )}
                          {isAlreadyAdded && (
                            <Chip
                              label="Already in section"
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={isAlreadyAdded}
                        onClick={() => onAddQuestion(sectionIndex, question)}
                      >
                        {isAlreadyAdded ? 'Added' : 'Add'}
                      </Button>
                    </Box>
                  </Card>
                  );
                })}
              </Box>

              <Box display="flex" justifyContent="center" mt={2}>
                <TablePagination
                  component="div"
                  count={questionsTotalCount}
                  page={questionsPage}
                  onPageChange={(_, newPage) => setQuestionsPage(newPage)}
                  rowsPerPage={questionsRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setQuestionsRowsPerPage(parseInt(e.target.value, 10));
                    setQuestionsPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </Box>
            </>
          )}
        </Box>
      </TabPanel>
    </Box>
  );
});

QuestionBankBrowser.displayName = 'QuestionBankBrowser';

export default QuestionBankBrowser;
